import React, { useState } from 'react';
import { useParams, Link } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';
import {
  useCustomer360,
  useCustomerSubscriptions,
  useCustomerReceipts,
  useBlacklistCustomer,
  useUpdateCustomer,
  useMarkCustomerDeceased,
  useEnableMobileAccess,
  useKycStatus,
  useDeleteKycDocument,
  useUploadKycDocument,
  useSubmitKyc
} from '../hooks';
import { StatusBadge } from '../../../components/ui/StatusBadge';
import { Button } from '../../../components/ui/Button';
import { Card, CardContent } from '../../../components/ui/Card';
import { Money } from '../../../components/ui/Money';
import { DataTable } from '@repo/web-list';
import { defineListPage } from '@repo/list';
import { defineDetailPage, useDetailPage } from '@repo/detail';
import { DetailPage } from '@repo/web-detail';
import type { SubscriptionListItem, ReceiptListItem, Customer360Detail, CustomerDetail } from '../types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/Dialog';
import { ResourceAuditTrail } from '../../audit/components/ResourceAuditTrail';
import { useForm } from '@tanstack/react-form';
import { z } from 'zod';
import { Input, FormField } from '../../../components/ui/FormField';
import { useToast } from '../../../components/ui/Toast';
import { PageLoader } from '../../../components/ui/PageLoader';
import { Banner } from '../../../components/ui/Banner';
import { DataView } from '../../../components/ui/DataView';
import { CheckCircle2, XCircle, AlertTriangle, UploadCloud, FileText, Trash2, ShieldAlert } from 'lucide-react';
import { InlineAlert } from '../../../components/ui/InlineAlert';
import {
  CustomerBanksTab,
  CustomerNomineesTab,
  CustomerEducationTab,
  CustomerOccupationTab,
  CustomerIntroducersTab,
  CustomerContactsTab,
  CustomerAddressesTab,
} from '../components/CustomerSubEntityTabs';
import { CustomerDocumentsTab } from '../components/CustomerDocumentsTab';

const updateSchema = z.object({
  name: z.string().min(2),
  email: z.string().email().optional().or(z.literal('')),
  bankAccountNumber: z.string().optional(),
  bankIfsc: z.string().optional(),
});

function ProfileTab({ customer }: { customer: CustomerDetail }) {
  const updateCustomer = useUpdateCustomer();
  const { toast } = useToast();
  const [showSensitive, setShowSensitive] = useState(false);

  const form = useForm({
    defaultValues: {
      name: customer.name || '',
      email: customer.email || '',
      bankAccountNumber: customer.bankAccountNumber || '',
      bankIfsc: customer.bankIfsc || '',
    },
    onSubmit: async ({ value }) => {
      try {
        await updateCustomer.mutateAsync({ id: customer.id, body: value });
        toast({ type: 'success', title: 'Profile updated' });
      } catch (err: unknown) {
        console.error('Failed to update profile', err);
        const msg = err instanceof Error ? err.message : 'Failed to update profile';
        toast({ type: 'error', title: 'Error', description: msg });
      }
    },
  });

  const isSubmitting = form.state.isSubmitting;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
      className="space-y-6 max-w-2xl mt-4"
    >
      <div className="flex justify-end">
        <Button variant="secondary" size="sm" type="button" onClick={() => setShowSensitive(!showSensitive)}>
          {showSensitive ? 'Hide Sensitive Data' : 'Reveal Sensitive Data'}
        </Button>
      </div>

      <Banner
        variant="info"
        title="1-to-Many Contacts, Addresses & Bank Accounts Supported"
        description="This customer supports multiple phone numbers, email addresses, physical addresses, and bank accounts/VPAs. Use the Contacts, Addresses, and Banks tabs above to manage all entries."
      />

      <div className="grid grid-cols-2 gap-4">
        <form.Field
          name="name"
          validators={{
            onChange: ({ value }) => {
              const r = updateSchema.shape.name.safeParse(value);
              return r.success ? undefined : r.error.issues[0]?.message;
            },
          }}
          children={(field) => {
            const hasErr = (form.state.submissionAttempts > 0 || field.state.meta.isBlurred) && field.state.meta.errors.length > 0;
            return (
              <FormField label="Full Name" htmlFor="prof-name" error={hasErr ? (field.state.meta.errors[0] as string | undefined) : undefined}>
                <Input
                  id="prof-name"
                  autoComplete="name"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.handleChange(e.target.value)}
                  disabled={isSubmitting}
                  error={hasErr}
                />
              </FormField>
            );
          }}
        />

        <FormField label="Mobile (Primary)" htmlFor="prof-mobile">
          <Input
            id="prof-mobile"
            type={showSensitive ? 'text' : 'password'}
            value={customer.mobile}
            disabled
          />
        </FormField>

        <FormField label="Mobile (Alternate)" htmlFor="prof-mobileAlt">
          <Input
            id="prof-mobileAlt"
            type={showSensitive ? 'text' : 'password'}
            value={customer.mobileAlt || ''}
            disabled
          />
        </FormField>
        <form.Field
          name="email"
          validators={{
            onChange: ({ value }) => {
              const r = updateSchema.shape.email.safeParse(value);
              return r.success ? undefined : r.error.issues[0]?.message;
            },
          }}
          children={(field) => {
            const hasErr = (form.state.submissionAttempts > 0 || field.state.meta.isBlurred) && field.state.meta.errors.length > 0;
            return (
              <FormField label="Email" htmlFor="prof-email" error={hasErr ? (field.state.meta.errors[0] as string | undefined) : undefined}>
                <Input
                  id="prof-email"
                  type="email"
                  autoComplete="email"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.handleChange(e.target.value)}
                  disabled={isSubmitting}
                  error={hasErr}
                />
              </FormField>
            );
          }}
        />
        <form.Field
          name="bankAccountNumber"
          children={(field) => (
            <FormField label="Bank Account Number (Primary)" htmlFor="prof-bankAcc">
              <Input
                id="prof-bankAcc"
                type={showSensitive ? 'text' : 'password'}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.handleChange(e.target.value)}
                disabled={isSubmitting}
              />
            </FormField>
          )}
        />
      </div>

      <div className="flex justify-end">
        <Button type="submit" variant="primary" loading={isSubmitting}>
          Save Changes
        </Button>
      </div>
    </form>
  );
}

function SubscriptionsTab({ customerId }: { customerId: string }) {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useCustomerSubscriptions(customerId, { page, limit: 10 });

  const subscriptionsConfig = defineListPage<SubscriptionListItem>({
    variant: 'data-table',
    resource: 'subscriptions',
    columns: [
      { id: 'schemeName', header: 'Scheme Name', accessor: 'schemeName', renderType: 'text' },
      { id: 'schemeNumber', header: 'Scheme Code', accessor: 'schemeNumber', renderType: 'code' },
      { id: 'instalmentAmount', header: 'Instalment Value', accessor: 'instalmentAmount', renderType: 'money', numeric: true },
      { id: 'status', header: 'Status', accessor: 'status', renderType: 'text' },
    ],
  });

  if (isLoading) return <PageLoader />;

  return (
    <div className="space-y-4">
      <DataTable
        config={subscriptionsConfig}
        data={data?.data || []}
        total={data?.meta?.total ?? 0}
        page={page}
        limit={10}
        onPageChange={setPage}
        customRenderers={{
          schemeName: (row: SubscriptionListItem) => (
            <Link to="/schemes/$id" params={{ id: row.schemeId }} className="text-accent-500 hover:underline font-medium">
              {row.schemeName}
            </Link>
          ),
          status: (row: SubscriptionListItem) => {
            const s = row.status;
            let variant: 'success' | 'warning' | 'danger' | 'info' = 'info';
            if (s === 'active') variant = 'success';
            if (s === 'prized') variant = 'warning';
            if (s === 'exited') variant = 'danger';
            return <StatusBadge status={variant} label={s} />;
          },
        }}
      />
    </div>
  );
}

function PaymentsTab({ customerId }: { customerId: string }) {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useCustomerReceipts(customerId, { page, limit: 10 });

  const paymentsConfig = defineListPage<ReceiptListItem>({
    variant: 'data-table',
    resource: 'receipts',
    columns: [
      { id: 'receiptNumber', header: 'Receipt No', accessor: 'receiptNumber', renderType: 'code' },
      { id: 'amount', header: 'Amount Collected', accessor: 'amount', renderType: 'money', numeric: true },
      { id: 'date', header: 'Date', accessor: 'date', renderType: 'datetime' },
      { id: 'status', header: 'Status', accessor: 'status', renderType: 'text' },
    ],
  });

  if (isLoading) return <PageLoader />;

  return (
    <div className="space-y-4">
      <DataTable
        config={paymentsConfig}
        data={data?.data || []}
        total={data?.meta?.total ?? 0}
        page={page}
        limit={10}
        onPageChange={setPage}
        customRenderers={{
          status: (row: ReceiptListItem) => {
            const s = row.status;
            const variant = s === 'ACTIVE' ? 'success' : 'danger';
            return <StatusBadge status={variant} label={s} />;
          },
        }}
      />
    </div>
  );
}

function CommunicationTab({ customer }: { customer: Customer360Detail }) {
  const notifications = (customer.notifications || []).map((n) => ({
    id: n.id,
    type: 'notification',
    title: `Notification sent (${n.channel})`,
    body: n.resolvedContent || `Template: ${n.templateKey}`,
    date: new Date(n.createdAt || new Date()),
    status: n.status,
  }));

  const crmActivities = (customer.crmSummary?.activities || []).map((a) => ({
    id: a.id,
    type: 'activity',
    title: `CRM Activity (${a.activityType})`,
    body: a.notes || `Outcome: ${a.outcome || 'N/A'}`,
    date: new Date(a.performedAt),
    status: 'COMPLETED',
  }));

  const timeline = [...notifications, ...crmActivities].sort((a, b) => b.date.getTime() - a.date.getTime());

  if (timeline.length === 0) {
    return (
      <div className="text-text-muted p-8 text-center border rounded-lg bg-surface-2 mt-4">
        No recent notifications or CRM activities recorded for this customer.
      </div>
    );
  }

  return (
    <div className="flow-root mt-6">
      <ul role="list" className="-mb-8">
        {timeline.map((item, itemIdx) => (
          <li key={item.id}>
            <div className="relative pb-8">
              {itemIdx !== timeline.length - 1 ? (
                <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-border" aria-hidden="true" />
              ) : null}
              <div className="relative flex space-x-3">
                <div className="flex items-center">
                  <span className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-background text-sm ${
                    item.type === 'notification' ? 'bg-info-bg text-info-500' : 'bg-success-bg text-success-500'
                  }`}>
                    {item.type === 'notification' ? '✉️' : '📞'}
                  </span>
                </div>
                <div className="flex-1 min-w-0 pt-1.5 flex justify-between space-x-4">
                  <div>
                    <p className="text-sm font-semibold text-text-primary">
                      {item.title}{' '}
                      <span className="text-xs text-text-muted font-normal ml-2">
                        ({item.status})
                      </span>
                    </p>
                    <p className="text-sm text-text-secondary mt-1 whitespace-pre-wrap">{item.body}</p>
                  </div>
                  <div className="text-right text-xs whitespace-nowrap text-text-muted">
                    <time dateTime={item.date.toISOString()}>
                      {item.date.toLocaleString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </time>
                  </div>
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface KycChecklistProps {
  memberId: string;
}

function KycChecklist({ memberId }: KycChecklistProps) {
  const { data: kycData, isLoading, isError, error, refetch } = useKycStatus(memberId);
  const deleteDocMutation = useDeleteKycDocument();
  const uploadDocMutation = useUploadKycDocument(memberId);
  const submitKycMutation = useSubmitKyc();
  const { toast } = useToast();

  // State for Photo upload
  const [photoFile, setPhotoFile] = useState<File | null>(null);

  // State for PAN upload
  const [panFile, setPanFile] = useState<File | null>(null);
  const [panNumber, setPanNumber] = useState('');
  const [panError, setPanError] = useState('');

  // State for Aadhaar/OVD upload
  const [ovdDocType, setOvdDocType] = useState<'AADHAAR' | 'PASSPORT' | 'VOTER_ID' | 'DRIVING_LICENSE'>('AADHAAR');
  const [ovdFile, setOvdFile] = useState<File | null>(null);
  const [ovdNumber, setOvdNumber] = useState('');
  const [ovdError, setOvdError] = useState('');

  // Upload state
  const [uploadingDocType, setUploadingDocType] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // PAN client validation
  const validatePan = (val: string) => {
    const clean = val.trim().toUpperCase();
    setPanNumber(clean);
    if (clean && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(clean)) {
      setPanError('Invalid PAN format (expected: ABCDE1234F)');
    } else {
      setPanError('');
    }
  };

  // OVD client validation
  const validateOvd = (val: string, type: string) => {
    const clean = val.trim();
    setOvdNumber(clean);
    if (type === 'AADHAAR') {
      const numeric = clean.replace(/\s/g, '');
      if (clean && !/^\d{12}$/.test(numeric)) {
        setOvdError('Aadhaar must be exactly 12 digits');
      } else {
        setOvdError('');
      }
    } else {
      setOvdError('');
    }
  };

  const handleUpload = async (docType: string, file: File | null, docNumber?: string) => {
    if (!file) {
      toast({
        type: 'error',
        title: 'Error',
        description: 'Please select a file to upload.',
      });
      return;
    }

    setUploadingDocType(docType);
    setUploadError(null);

    try {
      await uploadDocMutation.mutateAsync({ file, docType, docNumber });

      toast({
        type: 'success',
        title: 'Upload Successful',
        description: `${docType} document was uploaded.`,
      });

      // Reset local file inputs
      if (docType === 'PHOTO') setPhotoFile(null);
      else if (docType === 'PAN') {
        setPanFile(null);
        setPanNumber('');
      } else {
        setOvdFile(null);
        setOvdNumber('');
      }

      refetch();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Upload failed';
      console.error('Failed to upload', err);
      setUploadError(message);
      toast({
        type: 'error',
        title: 'Upload Failed',
        description: message,
      });
    } finally {
      setUploadingDocType(null);
    }
  };

  const handleDelete = async (docId: string, label: string) => {
    if (!confirm(`Are you sure you want to delete the ${label} document?`)) return;
    try {
      await deleteDocMutation.mutateAsync(docId);
      toast({
        type: 'success',
        title: 'Success',
        description: `${label} document deleted.`,
      });
      refetch();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete document';
      console.error('Delete failed', err);
      toast({
        type: 'error',
        title: 'Delete Failed',
        description: message,
      });
    }
  };

  const handleSubmitKyc = async () => {
    if (!kycData?.id) return;
    try {
      await submitKycMutation.mutateAsync(kycData.id);
      toast({
        type: 'success',
        title: 'KYC Submitted',
        description: 'KYC submission was successful and is now pending verification.',
      });
      refetch();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to submit KYC';
      console.error('Submission failed', err);
      toast({
        type: 'error',
        title: 'Submission Failed',
        description: message,
      });
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  if (isError) {
    return (
      <div className="p-8 text-center border rounded-lg bg-surface-2 mt-4 max-w-2xl mx-auto">
        <p className="text-danger-600 font-semibold mb-2">Error loading KYC status</p>
        <p className="text-xs text-text-secondary mb-4">{error instanceof Error ? error.message : 'Unknown error'}</p>
        <Button variant="secondary" size="sm" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  const status = kycData?.status || 'NOT_STARTED';
  const documents = kycData?.documents || [];
  const photoDoc = documents.find(d => d.docType === 'PHOTO');
  const panDoc = documents.find(d => d.docType === 'PAN');
  const ovdDoc = documents.find(d => ['AADHAAR', 'PASSPORT', 'VOTER_ID', 'DRIVING_LICENSE'].includes(d.docType));

  const allUploaded = !!photoDoc && !!panDoc && !!ovdDoc;
  const canModify = status === 'PENDING' || status === 'REJECTED' || !status;

  // Status styling configurations
  let statusBannerClass = 'bg-surface-2/40 border-border';
  let statusTitle = 'KYC Pending';
  let statusDescription = 'Please upload the required documents below to initiate the KYC verification process.';
  let statusIcon = <ShieldAlert className="w-5 h-5 text-text-secondary" />;

  if (status === 'COMPLETED' || (status as string) === 'VERIFIED') {
    statusBannerClass = 'bg-success-bg/10 border-success-bg/30';
    statusTitle = 'KYC Completed';
    statusDescription = `The customer KYC has been successfully verified. This member is cleared for all transactions.`;
    statusIcon = <CheckCircle2 className="w-5 h-5 text-success-500" />;
  } else if (status === 'REJECTED') {
    statusBannerClass = 'bg-danger-bg/10 border-danger-bg/30';
    statusTitle = 'KYC Rejected';
    statusDescription = 'The KYC submission was rejected. Reason: ' + (kycData?.rejectedReason || 'No reason provided') + '. Please delete the rejected files, upload corrected documents, and resubmit.';
    statusIcon = <XCircle className="w-5 h-5 text-danger-500" />;
  } else if (status === 'PENDING' && allUploaded) {
    statusBannerClass = 'bg-info-bg/10 border-info-bg/30';
    statusTitle = 'Ready for Verification';
    statusDescription = 'All required documents have been uploaded. Staff can review and mark the verification outcome.';
    statusIcon = <FileText className="w-5 h-5 text-info-500" />;
  }

  return (
    <div className="space-y-6 max-w-3xl mt-4">
      {/* Overall KYC status banner */}
      <div className={`border rounded-xl p-4 flex items-start gap-3 ${statusBannerClass}`}>
        <div className="shrink-0 mt-0.5">{statusIcon}</div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold text-text-primary">{statusTitle}</h4>
          <p className="text-xs text-text-secondary leading-relaxed">{statusDescription}</p>
        </div>
      </div>

      {uploadError && (
        <InlineAlert variant="danger" title="Upload Error" formErrors={[uploadError]} />
      )}

      {/* KYC Checklist Items */}
      <div className="space-y-4">
        {/* Item 1: Passport Photo */}
        <Card className="border border-border shadow-sm">
          <CardContent className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="mt-1">
                {photoDoc ? (
                  <CheckCircle2 className="w-5 h-5 text-success-500" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-text-muted" />
                )}
              </div>
              <div>
                <h5 className="text-sm font-semibold text-text-primary">Passport Size Photo</h5>
                <p className="text-xs text-text-secondary">Clear color photograph of the customer.</p>
                {photoDoc && (
                  <div className="mt-1 flex items-center gap-2 text-[11px] text-text-muted">
                    <span className="font-mono text-xs">Ref: {photoDoc.fileRef.slice(0, 12)}...</span>
                  </div>
                )}
              </div>
            </div>

            <div>
              {photoDoc ? (
                canModify && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-danger-500 hover:bg-danger-bg/10"
                    onClick={() => handleDelete(photoDoc.id, 'Photo')}
                    disabled={deleteDocMutation.isPending}
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" />
                    Delete
                  </Button>
                )
              ) : (
                canModify && (
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <Input
                      type="file"
                      id="photo-file"
                      accept=".jpg,.jpeg,.png"
                      className="hidden"
                      onChange={(e) => setPhotoFile((e.target as HTMLInputElement).files?.[0] || null)}
                    />
                    <label
                      htmlFor="photo-file"
                      className="inline-flex items-center justify-center px-3 py-1.5 border border-border rounded-lg text-xs font-medium cursor-pointer hover:bg-surface-2 text-text-secondary select-none"
                    >
                      <UploadCloud className="w-3.5 h-3.5 mr-1.5 text-text-muted" />
                      {photoFile ? photoFile.name : 'Choose File'}
                    </label>
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={!photoFile || uploadingDocType !== null}
                      loading={uploadingDocType === 'PHOTO'}
                      onClick={() => handleUpload('PHOTO', photoFile)}
                    >
                      Upload
                    </Button>
                  </div>
                )
              )}
            </div>
          </CardContent>
        </Card>

        {/* Item 2: PAN Card */}
        <Card className="border border-border shadow-sm">
          <CardContent className="p-4 flex flex-col justify-between gap-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="mt-1">
                  {panDoc ? (
                    <CheckCircle2 className="w-5 h-5 text-success-500" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-text-muted" />
                  )}
                </div>
                <div>
                  <h5 className="text-sm font-semibold text-text-primary">Permanent Account Number (PAN)</h5>
                  <p className="text-xs text-text-secondary">Official PAN Card issued by the Income Tax Department.</p>
                  {panDoc && (
                    <div className="mt-1 flex items-center gap-2 text-xs">
                      <span className="font-semibold text-text-primary">PAN: {panDoc.maskedDocNumber || panDoc.docNumber}</span>
                      <span className="text-text-muted">•</span>
                      <span className="font-mono text-text-muted text-[11px]">Ref: {panDoc.fileRef.slice(0, 12)}...</span>
                    </div>
                  )}
                </div>
              </div>

              <div>
                {panDoc ? (
                  canModify && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-danger-500 hover:bg-danger-bg/10"
                      onClick={() => handleDelete(panDoc.id, 'PAN')}
                      disabled={deleteDocMutation.isPending}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" />
                      Delete
                    </Button>
                  )
                ) : (
                  canModify && (
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                      <Input
                        type="file"
                        id="pan-file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="hidden"
                        onChange={(e) => setPanFile((e.target as HTMLInputElement).files?.[0] || null)}
                      />
                      <label
                        htmlFor="pan-file"
                        className="inline-flex items-center justify-center px-3 py-1.5 border border-border rounded-lg text-xs font-medium cursor-pointer hover:bg-surface-2 text-text-secondary select-none"
                      >
                        <UploadCloud className="w-3.5 h-3.5 mr-1.5 text-text-muted" />
                        {panFile ? panFile.name : 'Choose File'}
                      </label>
                      <Button
                        variant="primary"
                        size="sm"
                        disabled={!panFile || !panNumber || !!panError || uploadingDocType !== null}
                        loading={uploadingDocType === 'PAN'}
                        onClick={() => handleUpload('PAN', panFile, panNumber)}
                      >
                        Upload
                      </Button>
                    </div>
                  )
                )}
              </div>
            </div>

            {!panDoc && canModify && (
              <div className="max-w-xs mt-2 pl-8">
                <FormField label="PAN Number" htmlFor="pan-number" error={panError || undefined}>
                  <Input
                    id="pan-number"
                    placeholder="e.g. ABCDE1234F"
                    className="h-8 text-xs font-mono uppercase"
                    maxLength={10}
                    value={panNumber}
                    onChange={(e) => validatePan(e.target.value)}
                  />
                </FormField>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Item 3: Aadhaar or OVD */}
        <Card className="border border-border shadow-sm">
          <CardContent className="p-4 flex flex-col justify-between gap-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="mt-1">
                  {ovdDoc ? (
                    <CheckCircle2 className="w-5 h-5 text-success-500" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-text-muted" />
                  )}
                </div>
                <div>
                  <h5 className="text-sm font-semibold text-text-primary">Officially Valid Document (OVD)</h5>
                  <p className="text-xs text-text-secondary">Aadhaar Card, Passport, Voter ID or Driving License.</p>
                  {ovdDoc && (
                    <div className="mt-1 flex items-center gap-2 text-xs">
                      <span className="font-semibold text-text-primary">Type: {ovdDoc.docType}</span>
                      {ovdDoc.docNumber && (
                        <>
                          <span className="text-text-muted">•</span>
                          <span className="font-semibold text-text-primary">Doc No: {ovdDoc.maskedDocNumber || ovdDoc.docNumber}</span>
                        </>
                      )}
                      <span className="text-text-muted">•</span>
                      <span className="font-mono text-text-muted text-[11px]">Ref: {ovdDoc.fileRef.slice(0, 12)}...</span>
                    </div>
                  )}
                </div>
              </div>

              <div>
                {ovdDoc ? (
                  canModify && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-danger-500 hover:bg-danger-bg/10"
                      onClick={() => handleDelete(ovdDoc.id, 'OVD')}
                      disabled={deleteDocMutation.isPending}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" />
                      Delete
                    </Button>
                  )
                ) : (
                  canModify && (
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                      <Input
                        type="file"
                        id="ovd-file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="hidden"
                        onChange={(e) => setOvdFile((e.target as HTMLInputElement).files?.[0] || null)}
                      />
                      <label
                        htmlFor="ovd-file"
                        className="inline-flex items-center justify-center px-3 py-1.5 border border-border rounded-lg text-xs font-medium cursor-pointer hover:bg-surface-2 text-text-secondary select-none"
                      >
                        <UploadCloud className="w-3.5 h-3.5 mr-1.5 text-text-muted" />
                        {ovdFile ? ovdFile.name : 'Choose File'}
                      </label>
                      <Button
                        variant="primary"
                        size="sm"
                        disabled={!ovdFile || !ovdNumber || !!ovdError || uploadingDocType !== null}
                        loading={uploadingDocType === ovdDocType}
                        onClick={() => handleUpload(ovdDocType, ovdFile, ovdNumber)}
                      >
                        Upload
                      </Button>
                    </div>
                  )
                )}
              </div>
            </div>

            {!ovdDoc && canModify && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2 pl-8 max-w-lg">
                <FormField label="OVD Document Type" htmlFor="ovd-type">
                  <select
                    id="ovd-type"
                    className="w-full h-8 text-xs border border-border rounded-lg px-2 bg-background text-text-primary cursor-pointer focus:outline-none focus:ring-1 focus:ring-accent-500"
                    value={ovdDocType}
                    onChange={(e) => {
                      const type = e.target.value as 'AADHAAR' | 'PASSPORT' | 'VOTER_ID' | 'DRIVING_LICENSE';
                      setOvdDocType(type);
                      validateOvd(ovdNumber, type);
                    }}
                  >
                    <option value="AADHAAR">Aadhaar Card</option>
                    <option value="PASSPORT">Passport</option>
                    <option value="VOTER_ID">Voter ID Card</option>
                    <option value="DRIVING_LICENSE">Driving License</option>
                  </select>
                </FormField>
                <FormField label="Document Number" htmlFor="ovd-number" error={ovdError || undefined}>
                  <Input
                    id="ovd-number"
                    placeholder={ovdDocType === 'AADHAAR' ? '12-digit number' : 'Enter document number'}
                    className="h-8 text-xs font-mono"
                    value={ovdNumber}
                    onChange={(e) => validateOvd(e.target.value, ovdDocType)}
                  />
                </FormField>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* KYC Submit Action Button */}
      {canModify && (
        <div className="flex items-center justify-end pt-4 border-t border-border">
          <div className="flex items-center gap-3">
            {!allUploaded && (
              <span className="text-xs text-text-muted">
                * All documents in the checklist must be uploaded to submit KYC.
              </span>
            )}
            <Button
              variant="primary"
              size="md"
              disabled={!allUploaded || submitKycMutation.isPending || !kycData?.id}
              loading={submitKycMutation.isPending}
              onClick={handleSubmitKyc}
            >
              Submit KYC for Verification
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

const customerDetailConfig = defineDetailPage<Customer360Detail>({
  resource: 'customers',
  fetchById: async () => { throw new Error('fetchById not used directly'); },
  tabs: [
    { id: 'profile', label: 'Profile' },
    { id: 'subscriptions', label: 'Subscriptions' },
    { id: 'payments', label: 'Payments' },
    { id: 'communication', label: 'Communication Log' },
    { id: 'kyc', label: 'KYC' },
    { id: 'banks', label: 'Bank Accounts' },
    { id: 'nominees', label: 'Nominees' },
    { id: 'education', label: 'Education' },
    { id: 'occupation', label: 'Occupation' },
    { id: 'introducers', label: 'Introducers' },
    { id: 'contacts', label: 'Phone/Email' },
    { id: 'addresses', label: 'Addresses' },
    { id: 'documents', label: 'Documents' },
    { id: 'audit', label: 'Audit Trail' },
  ],
  defaultTab: 'profile',
});

export function CustomerProfilePage() {
  const { id } = useParams({ from: '/_auth/customers/$id' });
  const customerQuery = useCustomer360(id);
  const blacklistCustomer = useBlacklistCustomer();
  const markDeceased = useMarkCustomerDeceased();
  const enableMobileAccessMutation = useEnableMobileAccess();
  const { toast } = useToast();
  const detail = useDetailPage(customerDetailConfig, id, { enabled: false });
  const [blacklistReason, setBlacklistReason] = useState('');
  const [isBlacklistOpen, setIsBlacklistOpen] = useState(false);
  const [isDeceasedOpen, setIsDeceasedOpen] = useState(false);

  const deceasedForm = useForm({
    defaultValues: {
      deathDate: '',
      deathCertificate: null as File | null,
    },
    onSubmit: async ({ value }) => {
      try {
        await markDeceased.mutateAsync({
          id,
          payload: {
            deathDate: value.deathDate || undefined,
            deathCertificateUrl: undefined,
          },
        });
        toast({ type: 'success', title: 'Customer marked as deceased' });
        setIsDeceasedOpen(false);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to mark as deceased';
        console.error('Failed to mark customer as deceased', err);
        toast({ type: 'error', title: 'Failed to mark as deceased', description: message });
      }
    },
  });

  const handleBlacklist = (customer: Customer360Detail) => {
    if (!blacklistReason) return;
    blacklistCustomer.mutate({ id: customer.profile!.id, reason: blacklistReason }, {
      onSuccess: () => {
        setIsBlacklistOpen(false);
        toast({ type: 'success', title: 'Customer blacklisted successfully' });
      },
      onError: (err: Error) => {
        toast({ type: 'error', title: 'Failed to blacklist customer', description: err.message });
      }
    });
  };

  const statusBadge = (customer: Customer360Detail) => (
    <div className="flex flex-wrap items-center gap-2 mt-1">
      <span className="text-text-muted text-sm">{customer.profile?.customerCode || 'N/A'}</span>
      <StatusBadge status={customer.profile?.status === 'ACTIVE' ? 'success' : 'danger'} label={customer.profile?.status || ''} />
      <StatusBadge status="info" label={`KYC: ${customer.profile?.kycStatus === 'active' ? 'Verified' : customer.profile?.kycStatus || 'Pending'}`} />
      <StatusBadge status={customer.profile?.hasMobileAccess ? 'success' : 'warning'} label={`Mobile Access: ${customer.profile?.hasMobileAccess ? 'Active' : 'Disabled'}`} />
      {customer.financialSummary?.riskGrade && (
        <StatusBadge 
          status={
            customer.financialSummary.riskGrade === 'LOW' ? 'success' :
            customer.financialSummary.riskGrade === 'MEDIUM' ? 'warning' : 'danger'
          }
          label={`Risk: ${customer.financialSummary.riskGrade}`}
        />
      )}
    </div>
  );

  const headerActions = (customer: Customer360Detail) => (
    <>
      {customer.profile?.status !== 'BLACKLISTED' && (
        <>
          <Button
            variant="secondary"
            onClick={() => {
              detail.setActiveTab('profile');
              window.scrollTo({ top: 300, behavior: 'smooth' });
            }}
          >
            Edit Profile
          </Button>
          {!customer.profile?.hasMobileAccess && (
            <Button
              variant="primary"
              onClick={async () => {
                try {
                  await enableMobileAccessMutation.mutateAsync(customer.profile!.id);
                  toast({ type: 'success', title: 'Mobile access enabled successfully' });
                } catch (err: unknown) {
                  const message = err instanceof Error ? err.message : 'Failed to enable mobile access';
                  console.error('Failed to enable mobile access', err);
                  toast({ type: 'error', title: 'Failed to enable mobile access', description: message });
                }
              }}
              loading={enableMobileAccessMutation.isPending}
            >
              Enable Mobile Access
            </Button>
          )}
          <Button variant="danger" onClick={() => setIsDeceasedOpen(true)}>Mark as Deceased</Button>
          <Dialog open={isDeceasedOpen} onOpenChange={setIsDeceasedOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Mark Customer as Deceased</DialogTitle>
                <DialogDescription>
                  This action will initiate the deceased member handling process.
                </DialogDescription>
              </DialogHeader>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  deceasedForm.handleSubmit();
                }}
              >
                <div className="space-y-4 py-4">
                  <deceasedForm.Field
                    name="deathDate"
                    children={(field) => (
                      <FormField label="Date of Death">
                        <Input
                          type="date"
                          value={field.state.value}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.handleChange(e.target.value)}
                        />
                      </FormField>
                    )}
                  />
                  <deceasedForm.Field
                    name="deathCertificate"
                    children={(field) => (
                      <FormField label="Death Certificate Upload">
                        <Input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const file = e.target.files?.[0] || null;
                            field.handleChange(file);
                          }}
                        />
                      </FormField>
                    )}
                  />
                </div>
                <DialogFooter>
                  <Button variant="secondary" type="button" onClick={() => setIsDeceasedOpen(false)}>Cancel</Button>
                  <Button variant="danger" type="submit" disabled={deceasedForm.state.isSubmitting} loading={deceasedForm.state.isSubmitting}>
                    Confirm
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          <Button variant="danger" onClick={() => setIsBlacklistOpen(true)}>Blacklist</Button>
          <Dialog open={isBlacklistOpen} onOpenChange={setIsBlacklistOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Blacklist Customer</DialogTitle>
                <DialogDescription>
                  This will prevent the customer from starting new subscriptions. Existing subscriptions will remain active.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <FormField label="Reason for blacklisting">
                  <Input
                    value={blacklistReason}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBlacklistReason(e.target.value)}
                    placeholder="Enter reason..."
                  />
                </FormField>
              </div>
              <DialogFooter>
                <Button variant="secondary" onClick={() => setIsBlacklistOpen(false)}>Cancel</Button>
                <Button variant="danger" onClick={() => handleBlacklist(customer)} disabled={!blacklistReason || blacklistCustomer.isPending}>
                  {blacklistCustomer.isPending ? 'Blacklisting...' : 'Confirm Blacklist'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
    </>
  );

  return (
    <DataView query={customerQuery} loadingSkeleton={<DetailPage config={customerDetailConfig} detail={{ ...detail, isLoading: true, isError: false, error: null, refetch: () => {} }} title="Loading..." mainPane={null} />}>
      {(customer) => (
        <DetailPage
          config={customerDetailConfig}
          detail={{ ...detail, data: customer, isLoading: false, isError: false, error: null, refetch: customerQuery.refetch }}
          title={customer.profile?.name}
          backButton={<Link to="/customers" className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary transition-colors w-fit"><ChevronLeft className="h-4 w-4" />Back</Link>}
          statusBadge={statusBadge(customer)}
          headerActions={headerActions(customer)}
          mainPane={
            <div className="space-y-6">
              {customer.profile?.status === 'BLACKLISTED' && (
                <Banner
                  variant="warning"
                  title="Blacklisted Customer"
                  description="⚠️ This customer is blacklisted. They cannot be enrolled in new schemes."
                />
              )}
              {!customer.profile?.hasMobileAccess && (
                <Banner
                  variant="info"
                  title="Mobile App Registration Info"
                  // allow-raw-sql: instructional UI text, not a query
                  description={`This customer has no password. Ask them to open the mobile app, tap Register, and use mobile number ${customer.profile?.mobile} to receive an OTP and set their own MPIN.`}
                />
              )}
              {/* KPI Dashboard Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-xs font-semibold text-text-secondary uppercase">Active Chits</div>
                    <div className="text-2xl font-bold mt-1 text-text-primary">{customer.financialSummary?.activeChitsCount || 0}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-xs font-semibold text-text-secondary uppercase">Prized Chits</div>
                    <div className="text-2xl font-bold mt-1 text-text-primary">{customer.financialSummary?.prizedChitsCount || 0}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-xs font-semibold text-text-secondary uppercase">Outstanding Liability</div>
                    <div className="text-2xl font-bold mt-1 text-text-primary">
                      <Money paise={customer.financialSummary?.totalOutstandingLiability || 0} />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-xs font-semibold text-text-secondary uppercase">Overdue Amount</div>
                    <div className="text-2xl font-bold mt-1 text-danger-500">
                      <Money paise={customer.financialSummary?.totalOverdueAmount || 0} />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-xs font-semibold text-text-secondary uppercase">Guarantor Exposure</div>
                    <div className="text-2xl font-bold mt-1 text-warning-600">
                      <Money paise={customer.suretyExposure?.totalGuaranteedLiability || 0} />
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="mt-6">
                {detail.activeTab === 'profile' && customer.profile && <ProfileTab customer={customer.profile} />}
                {detail.activeTab === 'subscriptions' && <SubscriptionsTab customerId={id} />}
                {detail.activeTab === 'payments' && <PaymentsTab customerId={id} />}
                {detail.activeTab === 'communication' && <CommunicationTab customer={customer} />}
                {detail.activeTab === 'kyc' && <KycChecklist memberId={id} />}
                {detail.activeTab === 'banks' && <CustomerBanksTab customerId={id} />}
                {detail.activeTab === 'nominees' && <CustomerNomineesTab customerId={id} />}
                {detail.activeTab === 'education' && <CustomerEducationTab customerId={id} />}
                {detail.activeTab === 'occupation' && <CustomerOccupationTab customerId={id} />}
                {detail.activeTab === 'introducers' && <CustomerIntroducersTab customerId={id} />}
                {detail.activeTab === 'contacts' && <CustomerContactsTab customerId={id} />}
                {detail.activeTab === 'addresses' && <CustomerAddressesTab customerId={id} />}
                {detail.activeTab === 'documents' && <CustomerDocumentsTab customerId={id} />}
                {detail.activeTab === 'audit' && (
                  <div className="p-4 border border-surface-3 rounded-sm bg-surface-2">
                    <ResourceAuditTrail resourceType="customer" resourceId={customer.profile!.id} />
                  </div>
                )}
              </div>
            </div>
          }
        />
      )}
    </DataView>
  );
}
